import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PricingCalculatorSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    productionVolume: '',
    employeeCount: '',
    locations: '1',
    modules: []
  });
  const [quote, setQuote] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const translations = {
    en: {
      title: "Get Your Personalized Quote",
      subtitle: "Calculate exact pricing based on your specific requirements",
      form: {
        productionVolume: "Monthly Production Volume",
        employeeCount: "Number of Employees",
        locations: "Number of Locations",
        modules: "Required Modules"
      },
      placeholders: {
        productionVolume: "e.g., 1000 tons",
        employeeCount: "e.g., 50",
        locations: "Select locations"
      },
      modules: [
        { id: 'inventory', name: 'Inventory Management', price: 2000 },
        { id: 'production', name: 'Production Planning', price: 3000 },
        { id: 'quality', name: 'Quality Control', price: 2500 },
        { id: 'finance', name: 'Financial Management', price: 3500 },
        { id: 'analytics', name: 'Advanced Analytics', price: 4000 },
        { id: 'mobile', name: 'Mobile App', price: 1500 },
        { id: 'integration', name: 'Third-party Integration', price: 5000 }
      ],
      calculate: "Calculate Quote",
      quote: {
        title: "Your Personalized Quote",
        basePlan: "Base Plan",
        selectedModules: "Selected Modules",
        implementations: "Implementation",
        training: "Training & Support",
        total: "Total Monthly Cost",
        gst: "GST (18%)",
        grandTotal: "Grand Total",
        savings: "Annual Savings with 20% Discount",
        contact: "Contact Sales for Custom Quote"
      }
    },
    hi: {
      title: "अपना व्यक्तिगत कोट प्राप्त करें",
      subtitle: "अपनी विशिष्ट आवश्यकताओं के आधार पर सटीक मूल्य निर्धारण की गणना करें",
      form: {
        productionVolume: "मासिक उत्पादन मात्रा",
        employeeCount: "कर्मचारियों की संख्या",
        locations: "स्थानों की संख्या",
        modules: "आवश्यक मॉड्यूल"
      },
      placeholders: {
        productionVolume: "जैसे 1000 टन",
        employeeCount: "जैसे 50",
        locations: "स्थान चुनें"
      },
      modules: [
        { id: 'inventory', name: 'इन्वेंटरी प्रबंधन', price: 2000 },
        { id: 'production', name: 'उत्पादन योजना', price: 3000 },
        { id: 'quality', name: 'गुणवत्ता नियंत्रण', price: 2500 },
        { id: 'finance', name: 'वित्तीय प्रबंधन', price: 3500 },
        { id: 'analytics', name: 'एडवांस एनालिटिक्स', price: 4000 },
        { id: 'mobile', name: 'मोबाइल ऐप', price: 1500 },
        { id: 'integration', name: 'तृतीय-पक्ष एकीकरण', price: 5000 }
      ],
      calculate: "कोट की गणना करें",
      quote: {
        title: "आपका व्यक्तिगत कोट",
        basePlan: "बेस प्लान",
        selectedModules: "चयनित मॉड्यूल",
        implementations: "कार्यान्वयन",
        training: "प्रशिक्षण और सहायता",
        total: "कुल मासिक लागत",
        gst: "GST (18%)",
        grandTotal: "कुल योग",
        savings: "20% छूट के साथ वार्षिक बचत",
        contact: "कस्टम कोट के लिए सेल्स से संपर्क करें"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModuleChange = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId) 
        ? prev.modules.filter(id => id !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  const calculateQuote = () => {
    setIsCalculating(true);
    
    // Simulate calculation
    setTimeout(() => {
      const employeeCount = parseInt(formData.employeeCount) || 0;
      const locations = parseInt(formData.locations) || 1;
      
      // Base plan calculation
      let basePlan = 0;
      if (employeeCount <= 5) basePlan = 12000;
      else if (employeeCount <= 25) basePlan = 25000;
      else basePlan = 50000;
      
      // Module costs
      const selectedModulesCost = formData.modules.reduce((total, moduleId) => {
        const module = t.modules.find(m => m.id === moduleId);
        return total + (module?.price || 0);
      }, 0);
      
      // Additional costs
      const implementationCost = Math.floor(basePlan * 0.3);
      const trainingCost = Math.floor(basePlan * 0.2);
      const locationMultiplier = locations > 1 ? locations * 0.5 : 1;
      
      const subtotal = (basePlan + selectedModulesCost + implementationCost + trainingCost) * locationMultiplier;
      const gst = Math.floor(subtotal * 0.18);
      const grandTotal = subtotal + gst;
      const annualSavings = Math.floor(grandTotal * 12 * 0.2);
      
      setQuote({
        basePlan,
        selectedModulesCost,
        implementationCost,
        trainingCost,
        subtotal,
        gst,
        grandTotal,
        annualSavings,
        selectedModules: formData.modules.map(id => t.modules.find(m => m.id === id)?.name).filter(Boolean)
      });
      
      setIsCalculating(false);
    }, 1500);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <section id="pricing-calculator" className="py-20 bg-surface-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            <div className="space-y-6">
              {/* Production Volume */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t.form.productionVolume}
                </label>
                <Input
                  type="text"
                  value={formData.productionVolume}
                  onChange={(e) => handleInputChange('productionVolume', e.target.value)}
                  placeholder={t.placeholders.productionVolume}
                  className="w-full"
                />
              </div>

              {/* Employee Count */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t.form.employeeCount}
                </label>
                <Input
                  type="number"
                  value={formData.employeeCount}
                  onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                  placeholder={t.placeholders.employeeCount}
                  className="w-full"
                />
              </div>

              {/* Number of Locations */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {t.form.locations}
                </label>
                <select
                  value={formData.locations}
                  onChange={(e) => handleInputChange('locations', e.target.value)}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="1">1 Location</option>
                  <option value="2">2 Locations</option>
                  <option value="3">3 Locations</option>
                  <option value="4">4+ Locations</option>
                </select>
              </div>

              {/* Modules Selection */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-4">
                  {t.form.modules}
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {t.modules.map((module) => (
                    <div key={module.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id={module.id}
                          checked={formData.modules.includes(module.id)}
                          onChange={() => handleModuleChange(module.id)}
                          className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                        />
                        <label htmlFor={module.id} className="text-sm font-medium text-gray-900">
                          {module.name}
                        </label>
                      </div>
                      <div className="text-sm text-text-secondary">
                        {formatCurrency(module.price)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Calculate Button */}
              <Button
                onClick={calculateQuote}
                variant="primary"
                size="lg"
                loading={isCalculating}
                className="w-full bg-gradient-accent hover:bg-accent-600 shadow-cta"
                iconName="Calculator"
                iconPosition="left"
              >
                {t.calculate}
              </Button>
            </div>
          </div>

          {/* Quote Results */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            {quote ? (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <Icon name="FileText" size={40} color="var(--color-primary)" className="mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-primary">
                    {t.quote.title}
                  </h3>
                </div>

                {/* Quote Breakdown */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <span className="text-text-secondary">{t.quote.basePlan}</span>
                    <span className="font-medium">{formatCurrency(quote.basePlan)}</span>
                  </div>
                  
                  {quote.selectedModules?.length > 0 && (
                    <div className="pb-2 border-b border-border">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-text-secondary">{t.quote.selectedModules}</span>
                        <span className="font-medium">{formatCurrency(quote.selectedModulesCost)}</span>
                      </div>
                      <ul className="text-sm text-text-secondary space-y-1 ml-4">
                        {quote.selectedModules.map((module, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <Icon name="Check" size={12} color="var(--color-primary)" />
                            <span>{module}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <span className="text-text-secondary">{t.quote.implementations}</span>
                    <span className="font-medium">{formatCurrency(quote.implementationCost)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <span className="text-text-secondary">{t.quote.training}</span>
                    <span className="font-medium">{formatCurrency(quote.trainingCost)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <span className="text-text-secondary">{t.quote.total}</span>
                    <span className="font-medium">{formatCurrency(quote.subtotal)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-2 border-b border-border">
                    <span className="text-text-secondary">{t.quote.gst}</span>
                    <span className="font-medium">{formatCurrency(quote.gst)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-4 bg-primary-50 rounded-lg px-4">
                    <span className="text-lg font-bold text-primary">{t.quote.grandTotal}</span>
                    <span className="text-2xl font-bold text-primary">{formatCurrency(quote.grandTotal)}</span>
                  </div>
                  
                  <div className="bg-success-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="TrendingUp" size={20} color="var(--color-success)" />
                      <span className="font-medium text-success">{t.quote.savings}</span>
                    </div>
                    <div className="text-2xl font-bold text-success">
                      {formatCurrency(quote.annualSavings)}
                    </div>
                  </div>
                </div>

                {/* Contact Button */}
                <Button
                  variant="primary"
                  className="w-full bg-gradient-accent hover:bg-accent-600 shadow-cta"
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  {t.quote.contact}
                </Button>
              </div>
            ) : (
              <div className="text-center py-12">
                <Icon name="Calculator" size={64} color="var(--color-surface-300)" className="mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-secondary mb-2">
                  {currentLanguage === 'en' ? 'Ready to Calculate?' : 'गणना के लिए तैयार?'}
                </h3>
                <p className="text-text-secondary">
                  {currentLanguage === 'en' ? 'Fill in your requirements to get a personalized quote' : 'व्यक्तिगत कोट प्राप्त करने के लिए अपनी आवश्यकताएं भरें'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingCalculatorSection;